type BaseContact = {
  name?: string;
  email: string;
};

export type CollapsedContact = Pick<BaseContact, 'name' | 'email'>;

/**
 * Коллега, сотрудник, пользователь
 */
export type MemberContact = {
  id: `member:${number}`;
  contact_group: 'member';

  [key: string]: any;
} & BaseContact;

/**
 * Синхронизированный, внешний контакт, т.е. привязанный к какой-либо авторизации, например к google учетке, которую юзер подключил в хф
 */
export type AuthContact = {
  id: `auth_contact:${number}`;
  contact_group: 'auth_contact';

  [key: string]: any;
} & BaseContact;

/**
 * То же, что и auth_contact, только это какие-то ресурсы, например переговорки
 */
export type AuthResourceContact = {
  id: `auth_resource:${number}`;
  contact_group: 'auth_resource';

  [key: string]: any;
} & BaseContact;

/**
 * Пользовательский контакт, который пользователь добавил руками
 */
export type UserContact = {
  id: `user_contact:${number}`;
  contact_group: 'user_contact';

  [key: string]: any;
} & BaseContact;

/**
 * Контакт, тип которого определить не смогли.
 * Необходимо для случаев, когда происходят миграции в бд или что-то еще (в эти моменты поле contact_group может не прийти вообще).
 * Либо для любых новых типов контактов, которые еще не участвуют в схлопывании
 */
export type UnknownContact = {
  id: `${string}:${number}`;
  contact_group?: string | null;
} & BaseContact;

export type Contact = MemberContact | AuthContact | AuthResourceContact | UserContact | UnknownContact;

type MakeWithCollapsed<C extends Contact> = C & { collapsedContacts: CollapsedContact[] };

export type WithCollapsedMemberContact = MakeWithCollapsed<MemberContact>;
export type WithCollapsedAuthContact = MakeWithCollapsed<AuthContact>;
export type WithCollapsedAuthResourceContact = MakeWithCollapsed<AuthResourceContact>;
export type WithCollapsedUserContact = MakeWithCollapsed<UserContact>;
type WithCollapsedUnknownContact = MakeWithCollapsed<UnknownContact>;

export type WithCollapsedContact =
  | WithCollapsedMemberContact
  | WithCollapsedAuthContact
  | WithCollapsedAuthResourceContact
  | WithCollapsedUserContact
  | WithCollapsedUnknownContact;

export function isMember(contact: Contact): contact is MemberContact | WithCollapsedMemberContact {
  return contact.contact_group === 'member';
}

export function isAuth(contact: Contact): contact is AuthContact | WithCollapsedAuthContact {
  return contact.contact_group === 'auth_contact';
}

export function isUser(contact: Contact): contact is UserContact | WithCollapsedUserContact {
  return contact.contact_group === 'user_contact';
}

export function isAuthResource(
  contact: Contact
): contact is AuthResourceContact | WithCollapsedAuthResourceContact {
  return contact.contact_group === 'auth_resource';
}

export function isUnknown(contact: Contact): contact is UnknownContact | WithCollapsedUnknownContact {
  const isAnyContact = isMember(contact) || isAuth(contact) || isUser(contact) || isAuthResource(contact);
  return contact.contact_group === null || contact.contact_group === undefined || !isAnyContact;
}

/**
 * Преобразует контакт в облегченную "схлопанную" версию.
 * Используется для логики скрытия контактов, чистоты кода и небольшой оптимизации, чтобы не хранить ненужные данные в оперативной памяти клиента. Т.е. хранит только то, что в последствии нужно отправить бэку
 */
export function toCollapsedContact(contact: WithCollapsedContact | Contact): CollapsedContact {
  return {
    email: contact.email,
    name: contact.name
  };
}

export function isSameContacts(
  contactA: Contact | WithCollapsedContact | CollapsedContact,
  contactB: Contact | WithCollapsedContact | CollapsedContact
) {
  return contactA.email === contactB.email && contactA.name === contactB.name;
}
